<script setup>
import {
  computed,
  onMounted
} from "vue";
import {
  useRouter
} from "vue-router";

import {
  LEADERBOARD_TYPES,
  useLeaderboardStore
} from "@/stores/leaderboardStore.js";
import {
  useLanguageStore
} from "@/stores/languageStore.js";


const router =
  useRouter();

const leaderboardStore =
  useLeaderboardStore();

const languageStore =
  useLanguageStore();


const activeType =
  computed(
    () =>
      leaderboardStore
        .activeType
  );

const rows =
  computed(
    () =>
      leaderboardStore.rows
  );

const loading =
  computed(
    () =>
      leaderboardStore.loading
  );

const isProgressRanking =
  computed(
    () =>
      activeType.value
      === LEADERBOARD_TYPES
        .SYSTEM_PROGRESS
  );


const rankingTabs =
  computed(
    () => [
      {
        type:
          LEADERBOARD_TYPES
            .COUNTRY_SCORE,

        label:
          languageStore.t(
            "leaderboardScore"
          )
      },
      {
        type:
          LEADERBOARD_TYPES
            .SYSTEM_PROGRESS,

        label:
          languageStore.t(
            "leaderboardCleared"
          )
      }
    ]
  );


const pageTitle =
  computed(
    () =>
      languageStore.t(
        "leaderboardPageTitle"
      )
  );


const fullBoardTitle =
  computed(
    () =>
      languageStore.t(
        "leaderboardFullTitle"
      )
  );


function countryName(
  row
) {
  const code =
    String(
      row?.countryCode
      ?? ""
    )
      .trim()
      .toUpperCase();

  if (!code) {
    return "";
  }

  try {
    const locale =
      document
        .documentElement
        .lang
      || "en";

    const displayNames =
      new Intl.DisplayNames(
        [locale],
        {
          type: "region"
        }
      );

    return (
      displayNames.of(code)
      || code
    );
  } catch {
    return code;
  }
}


function displayName(
  row
) {
  return countryName(
    row
  );
}


function metricText(
  row,
  withLabel = true
) {
  if (
    isProgressRanking.value
  ) {
    const cleared =
      Number(
        row?.clearedLevels
      )
      || 0;

    if (!withLabel) {
      return cleared.toLocaleString(
        "en-US"
      );
    }

    return (
      `${languageStore.t(
        "leaderboardCleared"
      )}: ${cleared.toLocaleString("en-US")}`
    );
  }

  const score =
    Number(
      row?.score
    )
    || 0;

  if (!withLabel) {
    return score.toLocaleString(
      "en-US"
    );
  }

  return (
    `${languageStore.t(
      "leaderboardScore"
    )}: ${score.toLocaleString("en-US")}`
  );
}


function backHome() {
  router.push({
    name: "home"
  });
}


async function switchRanking(
  type
) {
  if (
    type
    === activeType.value
  ) {
    return;
  }

  try {
    await leaderboardStore
      .load(type);
  } catch (error) {
    console.warn(
      "Leaderboard request failed.",
      error
    );
  }
}


const first =
  computed(
    () =>
      rows.value.find(
        row =>
          row.rank === 1
      )
  );

const second =
  computed(
    () =>
      rows.value.find(
        row =>
          row.rank === 2
      )
  );

const third =
  computed(
    () =>
      rows.value.find(
        row =>
          row.rank === 3
      )
  );


function flagClass(row) {
  const code = String(row?.countryCode || "").trim().toLowerCase();
  return /^[a-z]{2}$/.test(code) ? `fi fi-${code}` : "fi fi-xx";
}


onMounted(
  async () => {
    try {
      await leaderboardStore
        .load(
          LEADERBOARD_TYPES
            .COUNTRY_SCORE
        );
    } catch (error) {
      console.warn(
        "Leaderboard request failed.",
        error
      );
    }
  }
);
</script>

<template>
  <section class="cosmic-leaderboard-page">
    <div class="star-bg" aria-hidden="true"></div>

    <div class="leaderboard-container">
      <div class="leaderboard-topbar">
        <button class="back-button leaderboard-back" type="button" @click="backHome">
          ← {{ languageStore.t('back') }}
        </button>

        <div class="leaderboard-top-actions" :aria-label="languageStore.t('leaderboard')">
          <button
            v-for="tab in rankingTabs"
            :key="tab.type"
            class="leaderboard-top-switch"
            :class="{ 'is-active': activeType === tab.type }"
            type="button"
            :aria-pressed="activeType === tab.type"
            @click.stop.prevent="switchRanking(tab.type)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <h1>{{ pageTitle }}</h1>

      <p
        v-if="loading"
        class="leaderboard-loading"
      >
        {{ languageStore.t('energyLoading') }}
      </p>

      <div class="podium-layout">
        <div v-if="second" class="podium-card podium-card--second float-2">
          <div class="flag-box flag-box--side">
            <span class="country-flag" :class="flagClass(second)" role="img" :aria-label="displayName(second)"></span>
          </div>
          <div class="podium-copy podium-copy--side">
            <h3>{{ displayName(second) }}</h3>
            <p>{{ metricText(second) }}</p>
          </div>
          <div class="podium-block podium-block--second"><span>2</span></div>
        </div>

        <div v-if="first" class="podium-card podium-card--first float-1">
          <div class="flag-box flag-box--first">
            <span class="country-flag" :class="flagClass(first)" role="img" :aria-label="displayName(first)"></span>
          </div>
          <div class="podium-copy podium-copy--first">
            <h3>{{ displayName(first) }}</h3>
            <p>{{ metricText(first) }}</p>
          </div>
          <div class="podium-block podium-block--first"><span>1</span></div>
        </div>

        <div v-if="third" class="podium-card podium-card--third float-3">
          <div class="flag-box flag-box--side">
            <span class="country-flag" :class="flagClass(third)" role="img" :aria-label="displayName(third)"></span>
          </div>
          <div class="podium-copy podium-copy--side">
            <h3>{{ displayName(third) }}</h3>
            <p>{{ metricText(third) }}</p>
          </div>
          <div class="podium-block podium-block--third"><span>3</span></div>
        </div>
      </div>

      <div class="section-divider"></div>

      <div class="full-board">
        <h2>{{ fullBoardTitle }}</h2>
        <div class="ranking-list">
          <div v-for="row in rows" :key="`${activeType}-${row.rank}`" class="ranking-row">
            <div class="country-cell">
              <span class="country-flag" :class="flagClass(row)" role="img" :aria-label="displayName(row)"></span>
              <span>{{ displayName(row) }}</span>
            </div>
            <span class="row-score">{{ metricText(row, false) }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/*
 * 本页不依赖 Tailwind CDN；以下 CSS 逐项还原用户给定 HTML/Tailwind 样式，
 * 避免 Vue/Vite 项目在离线或微信 WebView 中依赖运行时 CDN。
 */
.cosmic-leaderboard-page {
  position: relative;
  z-index: 0;
  width: calc(100vw - 24px);
  min-height: 100vh;
  margin-left: 50%;
  transform: translateX(-50%);
  padding: 34px 12px 48px;
  color: #fff;
  font-family: Inter, "Segoe UI", "Microsoft YaHei UI", "Microsoft YaHei", sans-serif;
  overflow: visible;
}

.star-bg {
  position: fixed;
  inset: 0;
  background-color: #050819;
  background-image:
    radial-gradient(2px 2px at 20px 30px, #fff, transparent),
    radial-gradient(2px 2px at 40px 80px, #fff, transparent),
    radial-gradient(1px 1px at 80px 30px, #fff, transparent);
  background-size: 220px 220px;
  opacity: .55;
  z-index: -1;
  pointer-events: none;
}

.leaderboard-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1152px;
  margin: 0 auto;
}

.leaderboard-topbar {
  position: relative;
  z-index: 3;
  min-height: 44px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.leaderboard-back {
  position: static;
  flex: 0 0 auto;
}

.leaderboard-top-actions {
  position: relative;
  z-index: 50;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
}

.leaderboard-top-switch {
  position: relative;
  z-index: 51;
  pointer-events: auto;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  min-width: 68px;
  height: 38px;
  padding: 0 15px;
  border: 1px solid rgba(96, 234, 235, .58);
  border-radius: 999px;
  background: rgba(4, 30, 47, .72);
  color: rgba(226, 245, 250, .86);
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color .18s ease, border-color .18s ease, color .18s ease, transform .18s ease;
}

.leaderboard-top-switch:hover {
  border-color: rgba(108, 247, 244, .88);
  color: #fff;
}

.leaderboard-top-switch.is-active {
  border-color: rgba(112, 255, 248, .96);
  background: linear-gradient(180deg, rgba(18, 116, 127, .88), rgba(5, 58, 76, .90));
  color: #fff;
}

h1 {
  margin: 0 0 28px;
  text-align: center;
  font-size: clamp(2rem, 5vw, 3.6rem);
  line-height: 1;
  font-weight: 900;
  letter-spacing: .05em;
}

@keyframes floatCenter {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-14px); }
}

@keyframes floatLeft {
  0%, 100% { transform: translateY(8px); }
  50% { transform: translateY(-6px); }
}

@keyframes floatRight {
  0%, 100% { transform: translateY(12px); }
  50% { transform: translateY(-4px); }
}

.float-1 { animation: floatCenter 5s ease-in-out infinite; }
.float-2 { animation: floatLeft 5.4s ease-in-out infinite; }
.float-3 { animation: floatRight 6s ease-in-out infinite; }

.leaderboard-loading {
  margin: 14px 0 0;
  text-align: center;
  color: rgba(226, 245, 250, .78);
  font-size: 14px;
}

.podium-layout {
  height: 392px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 12px;
}

.podium-card {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.podium-card--second,
.podium-card--third {
  width: 208px;
}

.podium-card--first {
  width: 224px;
}

.flag-box {
  overflow: hidden;
  margin-bottom: 16px;
  border-radius: 4px;
}

.flag-box--side {
  width: 144px;
  aspect-ratio: 4 / 3;
}

.flag-box--first {
  width: 176px;
  aspect-ratio: 4 / 3;
}

.flag-box .country-flag {
  display: block;
  width: 100%;
  height: 100%;
  background-color: transparent;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
}

.podium-copy {
  text-align: center;
}

.podium-copy h3,
.podium-copy p {
  margin: 0;
}

.podium-copy--side h3 {
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
}

.podium-copy--side p {
  margin-top: 4px;
  color: #cbd5e1;
  font-size: 18px;
  line-height: 28px;
}

.podium-copy--first h3 {
  font-size: 30px;
  line-height: 36px;
  font-weight: 700;
}

.podium-copy--first p {
  margin-top: 4px;
  color: #fde68a;
  font-size: 20px;
  line-height: 28px;
}

.podium-block {
  width: 100%;
  margin-top: 24px;
  display: grid;
  place-items: center;
  border-radius: 6px 6px 0 0;
}

.podium-block--second {
  height: 80px;
  border: 1px solid rgba(253, 186, 116, .30);
  background: linear-gradient(to top, rgba(249, 115, 22, .50), rgba(251, 146, 60, .12));
}

.podium-block--second span {
  color: #fed7aa;
  font-size: 30px;
  line-height: 36px;
  font-weight: 700;
}

.podium-block--first {
  height: 128px;
  border: 1px solid rgba(252, 211, 77, .35);
  background: linear-gradient(to top, rgba(251, 191, 36, .55), rgba(251, 191, 36, .15));
}

.podium-block--first span {
  color: #fde68a;
  font-size: 36px;
  line-height: 40px;
  font-weight: 900;
}

.podium-block--third {
  height: 56px;
  border: 1px solid rgba(203, 213, 225, .30);
  background: linear-gradient(to top, rgba(148, 163, 184, .55), rgba(148, 163, 184, .15));
}

.podium-block--third span {
  color: #e2e8f0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
}

.section-divider {
  width: 100%;
  height: 1px;
  margin: 14px 0 22px;
  background: linear-gradient(to right, transparent, rgba(129, 140, 248, .40), transparent);
}

.full-board {
  width: 100%;
  padding: 24px;
  border: 1px solid rgba(99, 102, 241, .20);
  border-radius: 12px;
  background: rgba(30, 27, 75, .35);
  backdrop-filter: blur(8px);
}

.full-board h2 {
  margin: 0 0 24px;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ranking-row {
  min-width: 0;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  background: rgba(255, 255, 255, .05);
  transition: background-color .15s ease;
}

.ranking-row:hover {
  background: rgba(255, 255, 255, .10);
}

.country-cell {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 16px;
}

.country-cell .country-flag {
  flex: 0 0 auto;
  width: 32px;
  height: 24px;
  border-radius: 4px;
  background-color: transparent;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
}

.country-cell span {
  min-width: 0;
  white-space: nowrap;
}

.row-score {
  flex: 0 0 auto;
  margin-left: 16px;
  color: #cbd5e1;
}

@media (min-width: 900px) {
  .podium-layout {
    height: 448px;
    gap: 40px;
  }

  .podium-card--second,
  .podium-card--third {
    width: 240px;
  }

  .podium-card--first {
    width: 288px;
  }

  .flag-box--side {
    width: 176px;
    height: 112px;
  }

  .flag-box--first {
    width: 224px;
    height: 144px;
  }

  .podium-copy--side h3 {
    font-size: 30px;
    line-height: 36px;
  }

  .podium-copy--first h3 {
    font-size: 36px;
    line-height: 40px;
  }

  .podium-block--second { height: 96px; }
  .podium-block--first { height: 160px; }
  .podium-block--third { height: 64px; }

  .podium-block--second span {
    font-size: 36px;
    line-height: 40px;
  }

  .podium-block--first span {
    font-size: 48px;
    line-height: 1;
  }

  .podium-block--third span {
    font-size: 30px;
    line-height: 36px;
  }

  .full-board h2 {
    font-size: 24px;
    line-height: 32px;
  }
}

@media (min-width: 1024px) {
  .podium-layout { gap: 56px; }
}

/* 手机端：保持 2 / 1 / 3 三列领奖台结构，只按比例缩放，不改设计语言。 */
@media (max-width: 767px) {
  .cosmic-leaderboard-page {
    width: calc(100vw - 16px);
    min-height: 100dvh;
    padding: max(16px, env(safe-area-inset-top)) 8px max(24px, env(safe-area-inset-bottom));
    overflow-x: hidden;
  }

  .leaderboard-topbar {
    min-height: 42px;
    margin-bottom: 8px;
    gap: 8px;
  }

  .leaderboard-back {
    margin: 0;
    white-space: nowrap;
  }

  .leaderboard-top-actions {
    gap: 6px;
  }

  .leaderboard-top-switch {
    min-width: 58px;
    height: 36px;
    padding: 0 11px;
    font-size: 13px;
    white-space: nowrap;
  }

  h1 {
    margin-bottom: 16px;
    font-size: clamp(1.55rem, 7.2vw, 2.05rem);
    line-height: 1.12;
    letter-spacing: .035em;
  }

  .podium-layout {
    width: 100%;
    height: 282px;
    gap: 6px;
    align-items: flex-end;
    justify-content: center;
  }

  .podium-card--second,
  .podium-card--third {
    width: min(28.5vw, 112px);
  }

  .podium-card--first {
    width: min(32vw, 126px);
  }

  .flag-box {
    margin-bottom: 9px;
    border-radius: 3px;
  }

  .flag-box--side {
    width: min(23vw, 90px);
    height: min(15.3vw, 60px);
  }

  .flag-box--first {
    width: min(27vw, 106px);
    height: min(17.2vw, 68px);
  }

  .podium-copy {
    width: 100%;
    padding: 0 2px;
  }

  .podium-copy--side h3 {
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: clamp(12px, 3.45vw, 15px);
    line-height: 1.12;
    overflow-wrap: anywhere;
  }

  .podium-copy--side p {
    margin-top: 3px;
    font-size: clamp(10px, 2.85vw, 12px);
    line-height: 1.25;
    white-space: nowrap;
  }

  .podium-copy--first h3 {
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: clamp(15px, 4.2vw, 19px);
    line-height: 1.1;
  }

  .podium-copy--first p {
    margin-top: 3px;
    font-size: clamp(11px, 3.15vw, 13px);
    line-height: 1.2;
    white-space: nowrap;
  }

  .podium-block {
    margin-top: 12px;
    border-radius: 5px 5px 0 0;
  }

  .podium-block--second { height: 58px; }
  .podium-block--first { height: 96px; }
  .podium-block--third { height: 42px; }

  .podium-block--second span {
    font-size: 24px;
    line-height: 1;
  }

  .podium-block--first span {
    font-size: 32px;
    line-height: 1;
  }

  .podium-block--third span {
    font-size: 21px;
    line-height: 1;
  }

  .section-divider {
    margin: 8px 0 14px;
  }

  .full-board {
    padding: 14px 12px;
    border-radius: 10px;
  }

  .full-board h2 {
    margin-bottom: 14px;
    font-size: 17px;
    line-height: 24px;
  }

  .ranking-list {
    gap: 8px;
  }

  .ranking-row {
    padding: 10px;
    gap: 8px;
  }

  .country-cell {
    gap: 10px;
  }

  .country-cell .country-flag {
    width: 28px;
    height: 21px;
  }

  .country-cell span {
    font-size: 14px;
    line-height: 20px;
  }

  .row-score {
    margin-left: 8px;
    font-size: 14px;
  }

  @keyframes floatCenter {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }

  @keyframes floatLeft {
    0%, 100% { transform: translateY(5px); }
    50% { transform: translateY(-3px); }
  }

  @keyframes floatRight {
    0%, 100% { transform: translateY(7px); }
    50% { transform: translateY(-2px); }
  }
}

@media (max-width: 390px) {
  .cosmic-leaderboard-page {
    width: calc(100vw - 12px);
    padding-left: 6px;
    padding-right: 6px;
  }

  h1 {
    margin-bottom: 12px;
    font-size: 1.45rem;
  }

  .podium-layout {
    height: 262px;
    gap: 4px;
  }

  .podium-card--second,
  .podium-card--third {
    width: 28vw;
  }

  .podium-card--first {
    width: 31vw;
  }

  .flag-box--side {
    width: 22vw;
    height: 14.7vw;
  }

  .flag-box--first {
    width: 26vw;
    height: 16.7vw;
  }

  .podium-copy--side h3 {
    min-height: 31px;
    font-size: 11.5px;
  }

  .podium-copy--side p {
    font-size: 9.5px;
  }

  .podium-copy--first h3 {
    min-height: 32px;
    font-size: 14.5px;
  }

  .podium-copy--first p {
    font-size: 10.5px;
  }

  .podium-block--second { height: 54px; }
  .podium-block--first { height: 88px; }
  .podium-block--third { height: 39px; }

  .full-board {
    padding: 12px 10px;
  }

  .ranking-row {
    padding: 9px 8px;
  }

  .country-cell {
    gap: 8px;
  }

  .country-cell span,
  .row-score {
    font-size: 13px;
  }
}


@media (orientation: landscape) and (max-height: 520px) and (max-width: 899px) {
  .cosmic-leaderboard-page {
    padding-top: max(12px, env(safe-area-inset-top));
  }

  h1 {
    margin-bottom: 10px;
    font-size: clamp(1.45rem, 4.2vw, 2rem);
  }

  .podium-layout {
    height: 255px;
  }

  .section-divider {
    margin: 12px 0 16px;
  }
}


/* 三榜切换补充：只有好友榜使用方形头像；总分榜与通关榜都保持国家国旗样式。 */












</style>
